import { useEffect, useReducer, useState } from 'react';
import { Bot } from 'lucide-react';
import { BackgroundField } from './components/BackgroundField';
import { TopMenu, type ViewKey } from './components/TopMenu';
import { CommandPalette, type JumpTarget } from './components/CommandPalette';
import { AskArgoDrawer } from './components/AskArgoDrawer';
import { DashboardView } from './views/DashboardView';
import { FilesView } from './views/FilesView';
import { ContractsView } from './views/ContractsView';
import { ToolsView } from './views/ToolsView';
import { ChatView } from './views/ChatView';
import { ContentEngineView } from './views/ContentEngineView';
import { EmailMarketingView } from './views/EmailMarketingView';
import { PeopleView, type ContactsTab, type PeopleFocus } from './views/PeopleView';
import { CustomerSuccessView } from './views/CustomerSuccessView';
import { ProjectsView, type ProjectsTab, type ProjectsFocus } from './views/ProjectsView';
import { TicketsView, type TicketFocus } from './views/TicketsView';
import { GoalsView } from './views/GoalsView';
import { ScoringEngineView } from './views/ScoringEngineView';
import { HyperCoderView } from './views/HyperCoderView';
import { ReportsView } from './views/ReportsView';
import { AdminView, type AdminTab } from './views/AdminView';
import { LoginView } from './views/LoginView';
import { FirstRunWizard } from './views/onboarding/FirstRunWizard';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuth } from './lib/auth/AuthProvider';
import { useAgentScheduler } from './lib/agent/agentScheduler';
import { useLiveSchedule } from './lib/agent/useLiveSchedule';
import { hashToView, viewToHash, viewFromHistoryState } from './lib/viewRoute';
import { shellClassName, SHELL_SCROLL_CLASS } from './lib/shellLayout';
import { dockReducer, initialDockState } from './lib/copilotDock';
import { useSessionGreeting } from './lib/agent/useSessionGreeting';
import { approveAllReversible } from './lib/agent/standupBoot';
import type { ContentItemType } from './lib/db/types';

/**
 * Dev-only crash trigger for demoing the per-view ErrorBoundary.
 *
 * Visit `?crash=<view>` (e.g. `?crash=tickets`) or `?crash=view` to crash
 * whichever view is open. Throws during render so the surrounding boundary
 * catches it and shows the fallback. "Try again" recovers because the
 * boundary's onReset strips the param (see clearCrash below). The whole
 * path is gated on import.meta.env.DEV, so it's dead-code-eliminated from
 * production builds and can never fire for a real user.
 */
function CrashTest({ when }: { when: boolean }) {
  if (when) {
    throw new Error('Intentional crash via ?crash — ErrorBoundary demo.');
  }
  return null;
}

export default function App() {
  const { state, user, hasRole } = useAuth();
  // Attio-style SPA history: the active view is mirrored into the URL hash
  // (#/tickets) so browser Back/Forward navigate BETWEEN views instead of
  // exiting the app. Initial view comes from the hash (deep-linkable).
  const [view, setView] = useState<ViewKey>(
    () => hashToView(window.location.hash) ?? 'dashboard',
  );
  // Deep-link state for the Contacts dropdown
  // (Accounts / Contacts / Prospects / Staff / Personas).
  const [contactsTab, setContactsTab] = useState<ContactsTab>('accounts');
  // Deep-link state for the Projects dropdown (Clients/Projects/Tasks/Agents/Development).
  const [projectsTab, setProjectsTab] = useState<ProjectsTab>('clients');
  // Deep-link state for the Admin dropdown (Users/Settings/Audit).
  const [adminTab, setAdminTab] = useState<AdminTab>('users');
  // Deep-link state for the Content Engine (Blog/SEO lands in the blog
  // workspace; plain "Content" clears back to the type picker). The value
  // keys the view so a repeat deep-link remounts into the right workspace.
  const [contentEngineType, setContentEngineType] = useState<ContentItemType | null>(null);
  // ⌘K command palette open state.
  const [paletteOpen, setPaletteOpen] = useState(false);
  // A query handed from the palette to the chat agent. The nonce lets
  // ChatView re-trigger a send even if the same text is asked twice.
  const [chatQuery, setChatQuery] = useState<{ text: string; nonce: number } | null>(null);
  // The persistent Argo Copilot dock (UX_REVIEW move #2): the agent as a
  // presence on EVERY view rather than a place you navigate to. Its
  // open/close/pulse machine is the pure tested reducer in lib/copilotDock —
  // collapsed by default with a subtle pulse until first opened, and never
  // reset by navigation. `drawerQuery` carries a handed-in question
  // (Dashboard "Ask Argo" buttons, ⌘K free text) into it.
  const [dock, dispatchDock] = useReducer(dockReducer, initialDockState);
  const [drawerQuery, setDrawerQuery] = useState<{ text: string; nonce: number } | null>(null);
  // Entity quick-jump focus. The palette can land directly on a record;
  // these carry the target into PeopleView / TicketsView. The nonce lets
  // the same record be re-focused twice (e.g. jump, close, jump again).
  const [peopleFocus, setPeopleFocus] = useState<PeopleFocus | null>(null);
  const [ticketFocus, setTicketFocus] = useState<TicketFocus | null>(null);
  const [projectsFocus, setProjectsFocus] = useState<ProjectsFocus | null>(null);
  // Bump to force a re-render after clearing the dev ?crash param so the
  // boundary's "Try again" actually recovers (see clearCrash / CrashTest).
  const [crashNonce, setCrashNonce] = useState(0);

  // The agent scheduler loop is mounted ONCE here, at the app root, so
  // deployed agents fire on their cadence regardless of which view is open
  // (true app-wide autonomy, not just while the Agents tab is visible). Its
  // state is threaded into the Agents tab for the master switch + ticker.
  const scheduler = useAgentScheduler();

  // Per-agent LIVE schedules tick here too — a 60s check that runs due
  // agents whose own `liveSchedule` flag is on, INDEPENDENT of the global
  // autonomy switch above. Safe-by-design: those runs only queue proposals
  // into the approvals inbox (they never write CRM data directly), and each
  // one lands on the Home "Argo has been working" feed with real counts.
  useLiveSchedule();

  // THE FRONT DOOR — every session opens with the assistant speaking first.
  // The hook gathers REAL context off the render path (standup counts, the
  // "while you were away" feed diff, live nudges, workspace emptiness),
  // composes the TOTAL session greeting, and auto-opens the dock exactly
  // once per page load. The once-per-day lastStandupShownAt stamp only
  // demotes the full standup to a lighter greeting — it NEVER suppresses
  // the open. Silence is a bug, not a state.
  const signedIn = state.status === 'signed-in';
  const userName = user?.displayName ?? undefined;
  const session = useSessionGreeting({
    signedIn,
    userName,
    // The reducer still refuses to hijack a dock the operator already
    // opened this load — and never re-opens after a manual close.
    onOpen: () => dispatchDock({ type: 'auto-standup' }),
  });

  // Every path that closes the drawer funnels here so a manual close of a
  // greeting-bearing drawer always settles it (stamps lastSeenFeedAt, and
  // the calendar day when a full standup was showing — no re-nag).
  const closeDock = () => {
    if (session.greeting) session.settle();
    dispatchDock({ type: 'close' });
  };

  // Navigate to a view AND record it in browser history (pushState) so
  // Back/Forward move within the app. Every in-app navigation funnels
  // through here (TopMenu, ⌘K palette, dashboard shortcuts, entity jumps).
  const navigate = (next: ViewKey) => {
    if (next !== view) {
      window.history.pushState({ view: next }, '', viewToHash(next));
    }
    setView(next);
    // Explicit contract: route changes never reset the Copilot dock — the
    // reducer returns the same state reference, so this is a no-op render.
    dispatchDock({ type: 'route-change' });
  };

  // History integration (P0 fix: Back was exiting the SPA).
  // - On mount, stamp the CURRENT history entry with its view + hash
  //   (replaceState) so Back to the first entry restores it too.
  // - popstate restores the view for that entry — the app root never
  //   unmounts, so all lifted state (drawer transcript, focuses) survives.
  useEffect(() => {
    const initial = hashToView(window.location.hash) ?? 'dashboard';
    window.history.replaceState(
      { ...(window.history.state ?? {}), view: initial },
      '',
      viewToHash(initial),
    );
    const onPop = (e: PopStateEvent) => {
      const restored =
        viewFromHistoryState(e.state) ??
        hashToView(window.location.hash) ??
        'dashboard';
      setView(restored);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Global ⌘K / Ctrl+K toggles the command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Route a query to the agent WITHOUT losing the operator's place: when
  // already on the Chat view, hand it to ChatView (with a fresh nonce) so it
  // auto-sends; anywhere else, open the docked Ask-Argo drawer beside the
  // current view instead of navigating away. WHY: "Ask Argo" affordances
  // live on entity surfaces (Dashboard findings, ⌘K free text) — the answer
  // belongs next to the thing being asked about (UX_REVIEW move #2).
  const askArgo = (text: string) => {
    if (view === 'chat') {
      setChatQuery({ text, nonce: Date.now() });
      return;
    }
    setDrawerQuery({ text, nonce: Date.now() });
    dispatchDock({ type: 'query' });
  };

  // Land directly on a record chosen in the palette. Each jump carries a
  // fresh nonce so the receiving view re-runs its focus effect every time.
  const jumpToEntity = (target: JumpTarget) => {
    const nonce = Date.now();
    if (target.kind === 'ticket') {
      setTicketFocus({ id: target.id, nonce });
      navigate('tickets');
      return;
    }
    if (target.kind === 'task' || target.kind === 'agent') {
      setProjectsFocus({ kind: target.kind, id: target.id, nonce });
      navigate('projects');
      return;
    }
    if (target.kind === 'account') {
      setPeopleFocus({ kind: 'account', id: target.id, nonce });
      navigate('contacts');
      return;
    }
    // person
    setPeopleFocus({ kind: 'person', id: target.id, tab: target.tab, nonce });
    navigate('contacts');
  };

  // Boot states — show the page chrome but a centered spinner where
  // content would go. Avoids a flash of "signed-out" for users with a
  // valid session.
  if (state.status === 'loading') {
    return (
      <>
        <BackgroundField />
        <div className="auth-boot">
          <div className="auth-boot__spinner" aria-label="Loading" />
        </div>
      </>
    );
  }

  if (state.status === 'signed-out') {
    return (
      <>
        <BackgroundField />
        <LoginView />
      </>
    );
  }

  // Belt + suspenders: if a non-admin somehow lands on Admin, drop
  // them to Dashboard so they don't see a half-rendered surface.
  // The TopMenu also hides the entry, so this is rarely hit.
  const safeView: ViewKey =
    view === 'admin' && !hasRole('admin') ? 'dashboard' : view;

  // Dev-only: read the ?crash target (recomputed when crashNonce bumps).
  // null in production — import.meta.env.DEV folds to false at build time.
  void crashNonce;
  const crashTarget = import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get('crash')
    : null;
  const crashThisView = crashTarget === 'view' || crashTarget === safeView;
  const clearCrash = () => {
    if (!import.meta.env.DEV) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('crash');
    // Keep the view stamped on this history entry so Back still restores it.
    window.history.replaceState({ view: safeView }, '', url);
    setCrashNonce((n) => n + 1);
  };

  return (
    <>
      <BackgroundField />
      <TopMenu
        active={safeView}
        onSelect={navigate}
        onSelectContactsTab={setContactsTab}
        onSelectProjectsTab={setProjectsTab}
        onSelectAdminTab={setAdminTab}
        onSelectContentType={setContentEngineType}
      />
      <main className={shellClassName({ fixed: safeView === 'projects' })}>
        {/* The ONE scroll container for every route (P0 fix: screens that
            don't scroll fully). The shell is a viewport-locked flex column;
            this child (flex:1, min-height:0, overflow-y:auto) is what
            actually scrolls, so every view reaches its bottom under the
            fixed TopMenu. On Projects the CSS variant turns its scrolling
            off and the kanban window scrolls internally instead. */}
        <div className={SHELL_SCROLL_CLASS}>
        {/* Per-view boundary: keyed on the active view so it auto-resets
            on navigation and a crashing surface can't take down the
            TopMenu / CommandPalette around it. */}
        <ErrorBoundary key={safeView} section={safeView} onReset={clearCrash}>
          <CrashTest when={crashThisView} />
          {safeView === 'dashboard' && (
            <DashboardView onNavigate={navigate} onSelectProjectsTab={setProjectsTab} onAskArgo={askArgo} />
          )}
          {safeView === 'files' && <FilesView />}
          {safeView === 'contracts' && <ContractsView />}
          {safeView === 'tools' && <ToolsView onLaunch={navigate} />}
          {safeView === 'chat' && <ChatView initialQuery={chatQuery} />}
          {safeView === 'content-engine' && (
            <ContentEngineView
              key={contentEngineType ?? 'picker'}
              initialContentType={contentEngineType}
            />
          )}
          {safeView === 'email-marketing' && <EmailMarketingView />}
          {safeView === 'contacts' && (
            <PeopleView initialTab={contactsTab} focus={peopleFocus} />
          )}
          {safeView === 'customer-success' && <CustomerSuccessView />}
          {safeView === 'projects' && (
            <ProjectsView initialTab={projectsTab} scheduler={scheduler} focus={projectsFocus} />
          )}
          {safeView === 'tickets' && <TicketsView focus={ticketFocus} />}
          {safeView === 'goals' && <GoalsView onNavigate={navigate} />}
          {safeView === 'hyper-coder' && <HyperCoderView />}
          {safeView === 'scoring-engine' && <ScoringEngineView />}
          {safeView === 'reports' && <ReportsView />}
          {safeView === 'admin' && (
            <AdminView
              initialTab={adminTab}
              onNavigate={navigate}
              onSelectProjectsTab={setProjectsTab}
            />
          )}
        </ErrorBoundary>
        </div>
      </main>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={navigate}
        onSelectContactsTab={setContactsTab}
        onSelectProjectsTab={setProjectsTab}
        onAskArgo={askArgo}
        onJump={jumpToEntity}
        canAdmin={hasRole('admin')}
      />
      {/* Persistent Argo Copilot dock: the floating "Ask Argo" launcher +
          the expandable chat drawer, mounted here at the app shell so the
          agent is reachable on EVERY route (including Chat — the dock is a
          presence, not a place). The launcher hides only while the drawer is
          open (the drawer has its own close), and pulses subtly until the
          first open (dock.pulse — cleared forever by the tested reducer).
          The drawer stays MOUNTED while closed so its transcript survives
          close/re-open and route changes within a session. */}
      {!dock.open && (
        <button
          type="button"
          className={`glass-btn glass-btn--primary${dock.pulse ? ' copilot-fab--pulse' : ''}`}
          onClick={() => dispatchDock({ type: 'open' })}
          title="Ask Argo — the copilot answers beside whatever you're looking at"
          style={{
            position: 'fixed',
            right: 20,
            bottom: 20,
            zIndex: 60,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '10px 14px',
            borderRadius: 999,
          }}
        >
          <Bot size={15} /> Ask Argo
        </button>
      )}
      <AskArgoDrawer
        open={dock.open}
        onClose={closeDock}
        view={safeView}
        query={drawerQuery}
        onOpenFullChat={() => {
          closeDock();
          navigate('chat');
        }}
        standup={session.greeting?.standup ?? null}
        onStandupReview={() => {
          // Walk focus to the first item's surface: the Home do-now queue.
          session.settle();
          dispatchDock({ type: 'close' });
          navigate('dashboard');
        }}
        onStandupApproveAll={() => {
          // Same batch lane as the Dashboard panel: sequential approval of
          // every reversible pending proposal; gated ones stay for review.
          session.settle();
          void approveAllReversible().catch(() => undefined);
        }}
        onStandupDismiss={() => {
          session.settle();
          dispatchDock({ type: 'close' });
        }}
        onStandupSetup={() => {
          // Cold start: take them straight to the guided setup that fills
          // the Brain and the book — the fuel every smart surface runs on.
          session.settle();
          dispatchDock({ type: 'close' });
          setAdminTab('setup');
          navigate('admin');
        }}
        greeting={session.greeting && !session.greeting.standup ? session.greeting : null}
        onGreetingDraftPosts={() => {
          // "Draft this week's posts" → the Content Engine, for real.
          session.settle();
          dispatchDock({ type: 'close' });
          navigate('content-engine');
        }}
        onGreetingSettle={session.settle}
      />
      {/* First-run onboarding: a full-screen overlay that self-gates on a
          truly cold store (zero accounts + zero people + no completed/
          skipped flag) and renders nothing otherwise — see
          src/views/onboarding/FirstRunWizard.tsx and src/lib/onboarding.ts.
          Mounted at the shell so it overlays every route. */}
      <FirstRunWizard />
    </>
  );
}
